// Raise a desktop notification for an in-app notification when the provider is
// away from the tab. Pulled out of useInAppNotifications so the conditional
// logic can be unit tested without mounting the full realtime hook (which
// depends on WebSocket, react-query, several stores, and the user context).

export interface FireDesktopNotificationArgs {
  title: string;
  body: string;
  /** Repeats sharing a tag collapse: a newer one replaces the prior notification
   *  instead of stacking. Callers scope it to the thing being notified about. */
  tag: string;
  /** Keep it up until the provider deals with it, instead of letting the OS
   *  hide it after a few seconds. Mirrors the alert's "keep on screen"
   *  preference so both surfaces make the same promise. */
  requireInteraction?: boolean;
  // What to run when the provider clicks the notification. The caller wires
  // this to whatever the in-app notification's own action does.
  onActivate?: () => void;
}

const ICON_PATH = "/pwa-icons/decoda-pwa-192x192.png";

// Notifications we put in the tray and have not seen dismissed. The tray
// outlives the React tree, so a notification raised under one tenant stays
// there — and stays clickable, running an action scoped to that tenant — long
// after the app has moved on. Callers close them on teardown.
const liveNotifications = new Set<Notification>();

/** Close every desktop notification this module still has a handle on. */
export function closeAllDesktopNotifications(): void {
  liveNotifications.forEach((notification) => {
    try {
      notification.close();
    } catch {
      // Already gone; nothing to do.
    }
  });
  liveNotifications.clear();
}

/** Whether the provider is away from the console right now. */
function isAwayFromTab(): boolean {
  if (typeof document === "undefined") return false;
  // `document.hidden` alone is too narrow: a browser window parked behind
  // another app still reports "visible" in Chrome, which is precisely the case
  // this exists for. Treat "window doesn't have focus" as away too.
  return document.hidden || !document.hasFocus();
}

export function fireDesktopNotification({
  title,
  body,
  tag,
  requireInteraction,
  onActivate,
}: FireDesktopNotificationArgs): boolean {
  if (!isAwayFromTab()) return false;
  if (typeof window === "undefined" || !("Notification" in window))
    return false;
  if (window.Notification.permission !== "granted") return false;

  try {
    const notification = new window.Notification(title, {
      body,
      tag,
      icon: ICON_PATH,
      requireInteraction,
      // A tagged notification replaces its predecessor *silently* by default,
      // so the second and later messages in a conversation would land with no
      // sound and no banner — the provider only ever hears about the first one.
      renotify: true,
    } as NotificationOptions);
    liveNotifications.add(notification);
    notification.onclick = () => {
      window.focus();
      onActivate?.();
      liveNotifications.delete(notification);
      notification.close();
    };
    // The provider can dismiss from the tray without ever clicking through, and
    // a closed notification is not ours to track any more.
    notification.onclose = () => liveNotifications.delete(notification);
    return true;
  } catch {
    // Some browsers throw if Notification is constructed in a tab that lost
    // permission between the permission check and the constructor call — fail
    // silent; the visible-tab path still has the in-app toast.
    return false;
  }
}
